import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { EXPENSE_STATUS_STYLES, INR } from "@/lib/constants";
import { Wallet, CheckCircle2, Clock, FileText, Filter } from "lucide-react";
import { usePagination, Pagination } from "@/components/Pagination";

function StatusBadge({ status }) {
  return <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.25em] border rounded-full ${EXPENSE_STATUS_STYLES[status] || ""}`}>{status}</span>;
}

function StatCard({ label, amount, count, icon: Icon }) {
  return (
    <div className="p-5 md:p-6 bg-white border border-[#EAE7E0]" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">{label}</div>
        {Icon && <Icon size={16} strokeWidth={1.5} className="text-[#C88270]" />}
      </div>
      <div className="font-serif text-2xl md:text-3xl tracking-tight text-[#2A2421]">{INR(amount)}</div>
      {count !== undefined && <div className="text-xs text-[#5C5652] mt-1">{count} item(s)</div>}
    </div>
  );
}

export default function ExpensesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return isAdmin ? <AdminExpenses /> : <TeamExpenses />;
}

/* ---------------------------------- TEAM ---------------------------------- */
function TeamExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/expenses"); setExpenses(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sum = (st) => expenses.filter((e) => e.status === st).reduce((s, e) => s + (e.amount || 0), 0);
  const paid = expenses.filter((e) => e.status === "paid");
  const filtered = tab === "all" ? expenses : expenses.filter((e) => e.status === tab);

  // Group paid by payment_date for "My Payouts"
  const payoutsByDate = {};
  paid.forEach((e) => { (payoutsByDate[e.payment_date || "—"] ||= []).push(e); });

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="expenses-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Your submissions</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421]">Expenses & Payouts</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <StatCard label="Draft" amount={sum("draft")} icon={FileText} />
        <StatCard label="Submitted" amount={sum("submitted")} icon={Clock} />
        <StatCard label="Approved" amount={sum("approved")} icon={CheckCircle2} />
        <StatCard label="Paid" amount={sum("paid")} icon={Wallet} />
      </div>

      <div className="flex gap-5 mb-6 border-b border-[#EAE7E0] overflow-x-auto">
        {["all", "draft", "submitted", "approved", "rejected", "payouts"].map((f) => (
          <button key={f} onClick={() => setTab(f)} data-testid={`filter-${f}`}
            className={`pb-3 text-xs uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${tab === f ? "text-[#C88270] border-b-2 border-[#C88270]" : "text-[#5C5652] hover:text-[#2A2421]"}`}>
            {f === "payouts" ? "My Payouts" : f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : tab === "payouts" ? (
        Object.keys(payoutsByDate).length === 0 ? (
          <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]">No payouts received yet.</div>
        ) : (
          <div className="space-y-6" data-testid="payouts-by-date">
            {Object.entries(payoutsByDate).sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([date, items]) => (
              <div key={date} className="bg-white border border-[#EAE7E0]">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAE7E0] bg-[#FAF9F6]">
                  <div className="text-sm font-medium text-[#2A2421]">Paid on {date}</div>
                  <div className="font-serif text-xl text-[#829384]">{INR(items.reduce((s, e) => s + e.amount, 0))}</div>
                </div>
                {items.map((ex) => (
                  <div key={ex.id} className="flex items-center justify-between px-6 py-3 border-b border-[#EAE7E0] last:border-0 text-sm">
                    <div><Link to={`/events/${ex.event_id}`} className="text-[#2A2421] hover:text-[#C88270]">{ex.event?.couple_names || ex.event?.title}</Link><span className="text-[#5C5652]"> · {ex.category}</span></div>
                    <div className="text-[#2A2421] font-medium">{INR(ex.amount)}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]">No expenses — add them from an event page.</div>
      ) : (
        <ExpenseTable rows={filtered} isAdmin={false} />
      )}
    </div>
  );
}

/* --------------------------------- ADMIN ---------------------------------- */
function AdminExpenses() {
  const confirm = useConfirm();
  const [tab, setTab] = useState("manage");
  const [expenses, setExpenses] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ team_member_id: "", status: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(new Set());
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payRef, setPayRef] = useState("");
  const [report, setReport] = useState(null);

  const loadMembers = useCallback(async () => {
    try { const { data } = await api.get("/users"); setMembers(data.filter((u) => u.role === "team_member")); } catch { /* ignore */ }
  }, []);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/expenses", { params });
      setExpenses(data);
      setSelected(new Set());
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [filters]);

  const loadReport = useCallback(async () => {
    try {
      const params = {};
      if (filters.team_member_id) params.team_member_id = filters.team_member_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/reports/payouts", { params });
      setReport(data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [filters]);

  useEffect(() => { loadMembers(); }, [loadMembers]);
  useEffect(() => { if (tab === "manage") loadExpenses(); else loadReport(); }, [tab, loadExpenses, loadReport]);

  const review = async (id, status) => {
    try { await api.patch(`/expenses/${id}/review`, { status }); toast.success(`Expense ${status}`); loadExpenses(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const approvedRows = expenses.filter((e) => e.status === "approved");
  const selectAllApproved = () => {
    if (selected.size === approvedRows.length) setSelected(new Set());
    else setSelected(new Set(approvedRows.map((e) => e.id)));
  };

  const markPaid = async () => {
    if (selected.size === 0) return toast.error("Select approved expenses to pay");
    if (!payDate) return toast.error("Choose a payment date");
    const total = approvedRows.filter((e) => selected.has(e.id)).reduce((s, e) => s + e.amount, 0);
    if (!(await confirm({ title: "Mark as paid?", description: `Record ${INR(total)} across ${selected.size} expense(s) as paid on ${payDate}.`, confirmText: "Mark paid" }))) return;
    try {
      const { data } = await api.post("/expenses/pay", { expense_ids: [...selected], payment_date: payDate, payout_reference: payRef || null });
      toast.success(`${data.paid} expense(s) marked paid`);
      setPayRef("");
      loadExpenses();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const totals = expenses.reduce((acc, e) => { acc[e.status] = (acc[e.status] || 0) + e.amount; return acc; }, {});

  const { page, setPage, totalPages, pageItems, total } = usePagination(expenses, 15, JSON.stringify(filters));

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="expenses-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Studio finances</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421]">Expenses & Payouts</h1>
      </div>

      <div className="flex gap-5 mb-6 border-b border-[#EAE7E0]">
        {[["manage", "Manage Expenses"], ["report", "Payout Report"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`pb-3 text-xs uppercase tracking-[0.2em] transition-colors ${tab === k ? "text-[#C88270] border-b-2 border-[#C88270]" : "text-[#5C5652] hover:text-[#2A2421]"}`}>{label}</button>
        ))}
      </div>

      {/* Filters */}
      <div className="p-4 bg-white border border-[#EAE7E0] mb-6 flex flex-wrap items-end gap-4" data-testid="expense-filters">
        <div className="flex items-center gap-2 text-[#5C5652]"><Filter size={16} /><span className="text-[10px] uppercase tracking-[0.2em]">Filters</span></div>
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] text-[#5C5652] mb-1">Team member</label>
          <select value={filters.team_member_id} onChange={(e) => setFilters({ ...filters, team_member_id: e.target.value })} data-testid="filter-member"
            className="border-b border-[#EAE7E0] bg-transparent py-1 text-sm focus:border-[#C88270] focus:outline-none min-w-[140px]">
            <option value="">All members</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        {tab === "manage" && (
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] text-[#5C5652] mb-1">Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status"
              className="border-b border-[#EAE7E0] bg-transparent py-1 text-sm focus:border-[#C88270] focus:outline-none">
              <option value="">All</option>
              {["draft", "submitted", "approved", "rejected", "paid"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] text-[#5C5652] mb-1">{tab === "report" ? "Paid from" : "From"}</label>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-from"
            className="border-b border-[#EAE7E0] bg-transparent py-1 text-sm focus:border-[#C88270] focus:outline-none" />
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] text-[#5C5652] mb-1">{tab === "report" ? "Paid to" : "To"}</label>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-to"
            className="border-b border-[#EAE7E0] bg-transparent py-1 text-sm focus:border-[#C88270] focus:outline-none" />
        </div>
        {(filters.team_member_id || filters.status || filters.date_from || filters.date_to) && (
          <button onClick={() => setFilters({ team_member_id: "", status: "", date_from: "", date_to: "" })} data-testid="clear-filters"
            className="text-[10px] uppercase tracking-[0.2em] text-[#C88270] pb-1">Clear</button>
        )}
      </div>

      {tab === "manage" ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            <StatCard label="Submitted" amount={totals.submitted || 0} icon={Clock} />
            <StatCard label="Approved" amount={totals.approved || 0} icon={CheckCircle2} />
            <StatCard label="Paid" amount={totals.paid || 0} icon={Wallet} />
            <StatCard label="Draft" amount={totals.draft || 0} icon={FileText} />
          </div>

          {/* Batch pay bar */}
          {approvedRows.length > 0 && (
            <div className="p-4 bg-[#FAF7F2] border border-[#829384]/40 mb-6 flex flex-wrap items-end gap-4" data-testid="batch-pay-bar">
              <button onClick={selectAllApproved} className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652] pb-1">
                {selected.size === approvedRows.length ? "Deselect all" : "Select all approved"}
              </button>
              <div>
                <label className="block text-[9px] uppercase tracking-[0.2em] text-[#5C5652] mb-1">Payment date</label>
                <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="pay-date"
                  className="border-b border-[#EAE7E0] bg-transparent py-1 text-sm focus:border-[#C88270] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[9px] uppercase tracking-[0.2em] text-[#5C5652] mb-1">Reference (optional)</label>
                <input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="UTR / note" data-testid="pay-ref"
                  className="border-b border-[#EAE7E0] bg-transparent py-1 text-sm focus:border-[#C88270] focus:outline-none placeholder:text-[#B8B2AA]" />
              </div>
              <button onClick={markPaid} data-testid="mark-paid-button"
                className="inline-flex items-center gap-2 bg-[#829384] text-white text-xs uppercase tracking-[0.2em] px-5 py-2 hover:bg-[#6d7c6f] transition-colors">
                <Wallet size={14} /> Mark {selected.size || ""} as paid
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
          ) : expenses.length === 0 ? (
            <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]">No expenses match these filters.</div>
          ) : (
            <>
              <ExpenseTable rows={pageItems} isAdmin selected={selected} onToggleSelect={toggleSelect} onReview={review} />
              <Pagination page={page} totalPages={totalPages} total={total} pageSize={15} onChange={setPage} testId="expenses-pagination" />
            </>
          )}
        </>
      ) : (
        <PayoutReport report={report} />
      )}
    </div>
  );
}

function PayoutReport({ report }) {
  if (!report) return <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>;
  return (
    <div data-testid="payout-report">
      <div className="p-6 bg-white border border-[#EAE7E0] mb-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-1">Total paid in range</div>
          <div className="font-serif text-4xl tracking-tight text-[#829384]">{INR(report.grand_total)}</div>
        </div>
        <div className="text-sm text-[#5C5652]">{report.member_count} team member(s)</div>
      </div>
      {report.members.length === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]">No payouts in this date range.</div>
      ) : (
        <div className="space-y-4">
          {report.members.map((m) => (
            <div key={m.team_member_id} className="bg-white border border-[#EAE7E0]" data-testid={`report-member-${m.team_member_id}`}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAE7E0] bg-[#FAF9F6]">
                <div>
                  <div className="font-serif text-xl tracking-tight text-[#2A2421]">{m.name}</div>
                  <div className="text-xs text-[#5C5652]">{m.count} payout(s)</div>
                </div>
                <div className="font-serif text-2xl text-[#829384]">{INR(m.total_paid)}</div>
              </div>
              {m.payouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-6 py-3 border-b border-[#EAE7E0] last:border-0 text-sm">
                  <div className="text-[#5C5652]">{p.payment_date} · {p.category}{p.payout_reference ? ` · ${p.payout_reference}` : ""}</div>
                  <div className="text-[#2A2421] font-medium">{INR(p.amount)}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExpenseTable({ rows, isAdmin, selected, onToggleSelect, onReview }) {
  return (
    <>
    <div className="md:hidden space-y-4" data-testid="expenses-mobile-list">
      {rows.map((ex) => (
        <div key={ex.id} data-testid={`expense-mcard-${ex.id}`}
          className="bg-white border border-[#EAE7E0] rounded-[20px] p-5 shadow-[0_2px_12px_-4px_rgba(42,36,33,0.05)]">
          <div className="flex items-start justify-between gap-3">
            <Link to={`/events/${ex.event_id}`} className="font-serif text-[20px] leading-tight text-[#2A2421] line-clamp-2">{ex.event?.couple_names || ex.event?.title || "—"}</Link>
            <StatusBadge status={ex.status} />
          </div>
          <div className="mt-1 text-[12px] text-[#5C5652]">{ex.event?.event_date}{ex.event?.slots?.length > 0 ? ` · ${ex.event.slots.join(", ")}` : ""}</div>
          {isAdmin && ex.team_member?.name && <div className="mt-1 text-[13px] text-[#5C5652]">{ex.team_member.name}</div>}
          <div className="mt-3 pt-3 border-t border-[#EAE7E0]/60 flex items-end justify-between">
            <div className="text-[13px] text-[#5C5652] pr-3">{ex.category}{ex.description ? ` · ${ex.description}` : ""}</div>
            <div className="font-serif text-xl text-[#2A2421] shrink-0">{INR(ex.amount)}</div>
          </div>
          {ex.payment_date && <div className="mt-1 text-[12px] text-[#829384]">Paid {ex.payment_date}</div>}
          {isAdmin && ex.status === "approved" && (
            <label className="mt-3 flex items-center gap-2 text-[13px] text-[#5C5652]">
              <input type="checkbox" checked={selected?.has(ex.id) || false} onChange={() => onToggleSelect(ex.id)} data-testid={`mselect-${ex.id}`} className="accent-[#829384] w-5 h-5" /> Select for payout
            </label>
          )}
          {isAdmin && ex.status === "submitted" && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={() => onReview(ex.id, "approved")} data-testid={`mapprove-${ex.id}`}
                className="min-h-[44px] rounded-xl text-xs uppercase tracking-[0.2em] border border-[#829384] text-[#829384] active:bg-[#829384] active:text-white transition-colors">Approve</button>
              <button onClick={() => onReview(ex.id, "rejected")} data-testid={`mreject-${ex.id}`}
                className="min-h-[44px] rounded-xl text-xs uppercase tracking-[0.2em] border border-red-700 text-red-700 active:bg-red-700 active:text-white transition-colors">Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
    <div className="hidden md:block bg-white border border-[#EAE7E0] overflow-x-auto" data-testid="expenses-table">
      <table className="w-full text-left min-w-[720px]">
        <thead>
          <tr className="border-b border-[#EAE7E0]">
            {isAdmin && <th className="py-4 px-4 w-8"></th>}
            <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Event</th>
            {isAdmin && <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Member</th>}
            <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Category</th>
            <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652] text-right">Amount</th>
            <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Status</th>
            <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Paid on</th>
            {isAdmin && <th className="py-4 px-6"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((ex) => (
            <tr key={ex.id} className="border-b border-[#EAE7E0] last:border-0" data-testid={`expense-row-${ex.id}`}>
              {isAdmin && (
                <td className="py-4 px-4">
                  {ex.status === "approved" && (
                    <input type="checkbox" checked={selected?.has(ex.id) || false} onChange={() => onToggleSelect(ex.id)} data-testid={`select-${ex.id}`}
                      className="accent-[#829384]" />
                  )}
                </td>
              )}
              <td className="py-4 px-6 text-sm">
                <Link to={`/events/${ex.event_id}`} className="text-[#2A2421] hover:text-[#C88270]">{ex.event?.couple_names || ex.event?.title || "—"}</Link>
                <div className="text-xs text-[#5C5652]">{ex.event?.event_date}</div>
                {ex.event?.slots?.length > 0 && <div className="text-xs text-[#C88270] mt-0.5" data-testid={`expense-slots-${ex.id}`}>Slot: {ex.event.slots.join(", ")}</div>}
              </td>
              {isAdmin && <td className="py-4 px-6 text-sm text-[#5C5652]">{ex.team_member?.name}</td>}
              <td className="py-4 px-6 text-sm text-[#5C5652]">{ex.category}{ex.description && <span className="text-[#5C5652]/70"> · {ex.description}</span>}</td>
              <td className="py-4 px-6 text-sm text-[#2A2421] text-right font-medium">{INR(ex.amount)}</td>
              <td className="py-4 px-6"><StatusBadge status={ex.status} /></td>
              <td className="py-4 px-6 text-sm text-[#5C5652]">{ex.payment_date || "—"}</td>
              {isAdmin && (
                <td className="py-4 px-6 text-right">
                  {ex.status === "submitted" && (
                    <div className="flex justify-end gap-2">
                      <button onClick={() => onReview(ex.id, "approved")} data-testid={`approve-${ex.id}`}
                        className="text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-[#829384] text-[#829384] hover:bg-[#829384] hover:text-white transition-colors">Approve</button>
                      <button onClick={() => onReview(ex.id, "rejected")} data-testid={`reject-${ex.id}`}
                        className="text-[10px] uppercase tracking-[0.2em] px-3 py-1 border border-red-700 text-red-700 hover:bg-red-700 hover:text-white transition-colors">Reject</button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}
